import { useState, useEffect, useRef, RefObject } from 'react';

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
  triggerOnce?: boolean;
}

interface UseIntersectionObserverReturn {
  ref: RefObject<HTMLDivElement>;
  isIntersecting: boolean;
  entry: IntersectionObserverEntry | null;
}

/**
 * Hook para detectar quando um elemento entra no viewport
 * Ideal para animações lazy e carregamento progressivo
 */
export const useIntersectionObserver = (
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn => {
  const {
    root = null,
    rootMargin = '0px',
    threshold = 0.3,
    freezeOnceVisible = false,
    triggerOnce = true
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Se já disparou e é triggerOnce, não fazer nada
    if (hasTriggered && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setEntry(entry);
        
        const isElementIntersecting = entry.isIntersecting;
        
        if (isElementIntersecting && !hasTriggered) {
          setHasTriggered(true);
        }

        // Se freezeOnceVisible está ativo e já foi visível, manter como true
        if (freezeOnceVisible && isIntersecting) return;

        setIsIntersecting(isElementIntersecting);
      },
      {
        root,
        rootMargin,
        threshold
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [root, rootMargin, threshold, freezeOnceVisible, triggerOnce, hasTriggered]);

  return { ref, isIntersecting, entry };
};

/**
 * Hook para múltiplos elementos com animação sequencial
 */
export const useSequentialIntersection = (
  itemCount: number,
  options: UseIntersectionObserverOptions = {}
) => {
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const itemRefs = useRef<(HTMLDivElement | null)[]>(Array(itemCount).fill(null));

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    itemRefs.current.forEach((element, index) => {
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => new Set([...prev, index]));
          }
        },
        {
          threshold: options.threshold || 0.3,
          rootMargin: options.rootMargin || '0px'
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [itemCount]);

  const setItemRef = (index: number) => (el: HTMLDivElement | null) => {
    itemRefs.current[index] = el;
  };

  return {
    visibleItems,
    setItemRef,
    isVisible: (index: number) => visibleItems.has(index)
  };
};

/**
 * Hook otimizado para listas grandes
 */
export const useViewportList = <T,>(
  items: T[],
  options: UseIntersectionObserverOptions = {}
) => {
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt((entry.target as HTMLElement).dataset.index || '0');
          
          if (entry.isIntersecting) {
            setVisibleIndices(prev => new Set([...prev, index]));
          } else if (!options.freezeOnceVisible) {
            setVisibleIndices(prev => {
              const newSet = new Set(prev);
              newSet.delete(index);
              return newSet;
            });
          }
        });
      },
      {
        root: options.root || container,
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '50px'
      }
    );

    // Observar todos os filhos diretos
    const children = container.children;
    Array.from(children).forEach((child, index) => {
      (child as HTMLElement).dataset.index = index.toString();
      observer.observe(child);
    });

    return () => observer.disconnect();
  }, [items.length, options]);

  return {
    containerRef,
    visibleIndices,
    isVisible: (index: number) => visibleIndices.has(index)
  };
};